/**
 * Customer cart — purely client-side. The backend has no cart concept;
 * the cart is materialized into a single POST /orders call at checkout.
 *
 * Persisted to localStorage so a refresh doesn't lose the cart.
 */

import { create } from 'zustand';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';

export interface CartLine {
  sku_id: number;
  sku_code: string;
  name: string;
  unit_price: number;
  image_url?: string | null;
  quantity: number;
}

interface CartState {
  items: CartLine[];
  addItem: (item: Omit<CartLine, 'quantity'>, qty?: number) => void;
  setQuantity: (sku_id: number, qty: number) => void;
  removeItem: (sku_id: number) => void;
  clear: () => void;
  totalItems: () => number;
  subtotal: () => number;
}

export const useCartStore = create<CartState>()(
  devtools(
    persist(
      (set, get) => ({
        items: [],
        addItem: (item, qty = 1) => {
          if (qty <= 0) return;
          set((s) => {
            const existing = s.items.find((l) => l.sku_id === item.sku_id);
            if (existing) {
              return {
                items: s.items.map((l) =>
                  l.sku_id === item.sku_id ? { ...l, quantity: l.quantity + qty } : l
                ),
              };
            }
            return {
              items: [
                ...s.items,
                {
                  sku_id: item.sku_id,
                  sku_code: item.sku_code,
                  name: item.name,
                  unit_price: item.unit_price,
                  image_url: item.image_url ?? null,
                  quantity: qty,
                },
              ],
            };
          });
        },
        setQuantity: (sku_id, qty) => {
          if (qty <= 0) {
            get().removeItem(sku_id);
            return;
          }
          set((s) => ({
            items: s.items.map((l) => (l.sku_id === sku_id ? { ...l, quantity: qty } : l)),
          }));
        },
        removeItem: (sku_id) =>
          set((s) => ({ items: s.items.filter((l) => l.sku_id !== sku_id) })),
        clear: () => set({ items: [] }),
        totalItems: () => get().items.reduce((sum, l) => sum + l.quantity, 0),
        subtotal: () =>
          get().items.reduce((sum, l) => sum + l.quantity * l.unit_price, 0),
      }),
      {
        name: 'vinpulse_customer_cart',
        storage: createJSONStorage(() => localStorage),
        partialize: (s) => ({ items: s.items }),
      }
    ),
    { name: 'cart-store' }
  )
);
